import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NullOr, RecordingMomentRoles, AIFunctionCall, AIFunctionCallOutput } from "@regal-voice/shared-types";

import { EnhancedRecordingMoment, RecordingDetails, TranscriptionItemParams, TranscriptTabs } from "Types/Transcripts";

export type TranscriptionParticipantInfo = {
    name: string;
    role: RecordingMomentRoles;
    /**
     * This value gets calculated by checking if the ref, which is an email for agents, is
     * on the recording `managerParticipants` field. It indicates that this participant joined
     * the conference as a manager, through a manager action (whisper/barge).
     */
    monitoring: boolean;
    /**
     * This comes from Ingest and, for contacts, it's the word "contact", but for agents, is their
     * email address, which allows us to map it against the `managerParticipants` field in the
     * recording and identify if this agent joined the conference as a manager.
     */
    ref: string;
};

export type TranscriptionMomentsGroup = {
    moments: EnhancedRecordingMoment[];
    participantId: string;
};

export type TranscriptionState = {
    error: NullOr<string>;
    loading: boolean;
    params: NullOr<TranscriptionItemParams>;
    activeTab: { tab: TranscriptTabs; id: NullOr<string> | undefined };
    groupedMoments: NullOr<Record<string, TranscriptionMomentsGroup>>;
    participants: Record<string, RecordingMomentRoles>;
    participantsInfo: Record<string, TranscriptionParticipantInfo>;
    recording: NullOr<RecordingDetails>;
    functionCalls: NullOr<Array<AIFunctionCall | AIFunctionCallOutput>>;
};

export const initialState: TranscriptionState = {
    loading: true,
    error: null,
    params: null,
    groupedMoments: null,
    functionCalls: null,
    participants: {},
    participantsInfo: {},
    recording: null,
    activeTab: { tab: TranscriptTabs.CALL_DETAILS, id: undefined },
};

const TranscriptionStateSlice = createSlice({
    name: "transcriptions",
    initialState,
    reducers: {
        setRecording(state, action: PayloadAction<RecordingDetails>) {
            state.recording = action.payload;
            state.loading = false;
        },
        setGroupedMoments(state, action: PayloadAction<Record<string, TranscriptionMomentsGroup>>) {
            state.groupedMoments = action.payload;
        },
        setFunctionCalls(state, action: PayloadAction<Array<AIFunctionCall | AIFunctionCallOutput>>) {
            state.functionCalls = action.payload;
        },
        setParticipants(
            state,
            action: PayloadAction<{
                participants: Record<string, RecordingMomentRoles>;
                participantsInfo: Record<string, TranscriptionParticipantInfo>;
            }>
        ) {
            const { participants, participantsInfo } = action.payload;
            state.participants = participants;
            state.participantsInfo = participantsInfo;
        },
        setParams(state, action: PayloadAction<TranscriptionItemParams>) {
            state.params = action.payload;
        },
        setActiveTab(state, action: PayloadAction<{ tab: TranscriptTabs; id?: NullOr<string> }>) {
            state.activeTab = { tab: action.payload.tab, id: action.payload.id };
        },
        setError(state, action: PayloadAction<NullOr<string>>) {
            state.error = action.payload;
            state.loading = false;
        },
        clearTranscription() {
            return { ...initialState };
        },
    },
});

export const {
    setRecording,
    setGroupedMoments,
    setFunctionCalls,
    setParticipants,
    setParams,
    setError,
    clearTranscription,
    setActiveTab,
} = TranscriptionStateSlice.actions;
export default TranscriptionStateSlice.reducer;
